/**
 * Install App page — permanent, always-accessible entry for installing the
 * haythix PWA.
 *
 * Background: Chrome/Edge auto-banners fire `beforeinstallprompt` once and then
 * vanish if dismissed. Users who close the banner have no way to find install
 * again unless they know to click the hidden address-bar icon. This page is
 * the always-on alternative — accessible from Tools menu.
 *
 * The `beforeinstallprompt` event is captured globally in main.jsx and stored
 * on `window.__pwaInstallPrompt` so this page can trigger install any time,
 * even hours after first page load.
 */

import { useEffect, useState } from 'react';
import {
  Download, Smartphone, Monitor, Check, AlertCircle, Share, Plus,
  Globe, ExternalLink, RefreshCw,
} from 'lucide-react';

function detectBrowser() {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isMac = /Macintosh/.test(ua);
  const isWindows = /Windows/.test(ua);

  // Detect browser engines in priority order
  if (/EdgiOS|EdgA|Edg\//.test(ua)) return { name: 'Edge', isIOS, isAndroid, isMac, isWindows, canPrompt: !isIOS };
  if (/CriOS/.test(ua)) return { name: 'Chrome iOS', isIOS: true, isAndroid: false, isMac: false, isWindows: false, canPrompt: false };
  if (/FxiOS/.test(ua)) return { name: 'Firefox iOS', isIOS: true, isAndroid: false, isMac: false, isWindows: false, canPrompt: false };
  if (/Firefox/.test(ua)) return { name: 'Firefox', isIOS, isAndroid, isMac, isWindows, canPrompt: false };
  if (/Chrome/.test(ua) && !/Edg/.test(ua)) return { name: 'Chrome', isIOS, isAndroid, isMac, isWindows, canPrompt: !isIOS };
  if (/Safari/.test(ua)) return { name: 'Safari', isIOS, isAndroid: false, isMac, isWindows: false, canPrompt: false };
  return { name: 'Unknown', isIOS, isAndroid, isMac, isWindows, canPrompt: false };
}

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export default function InstallAppPage() {
  const [browser] = useState(detectBrowser);
  const [installed, setInstalled] = useState(isStandalone);
  const [installable, setInstallable] = useState(!!window.__pwaInstallPrompt);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => { setInstalled(true); setInstallable(false); };
    window.addEventListener('pwa:installable', onInstallable);
    window.addEventListener('pwa:installed', onInstalled);
    // Recheck install state on focus (user may have installed elsewhere)
    const onFocus = () => setInstalled(isStandalone());
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('pwa:installable', onInstallable);
      window.removeEventListener('pwa:installed', onInstalled);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  async function handleInstall() {
    const prompt = window.__pwaInstallPrompt;
    if (!prompt) {
      setResult({ type: 'warn', msg: 'Install prompt not available right now. Try the manual steps below, or come back later — the browser sometimes needs a few minutes of activity before offering install.' });
      return;
    }
    setBusy(true);
    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') {
        setResult({ type: 'ok', msg: 'Installing — the app icon will appear on your taskbar / Apps menu in a few seconds.' });
        window.__pwaInstallPrompt = null;
        setInstallable(false);
      } else {
        setResult({ type: 'info', msg: 'Install cancelled. You can try again any time from this page.' });
      }
    } catch (err) {
      setResult({ type: 'warn', msg: `Couldn't open the install prompt: ${err.message}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto">
      <header>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Download className="w-6 h-6 text-accent-primary" /> Install haythix
        </h1>
        <p className="text-sm text-gray-400 mt-1">
          Pin haythix to your desktop, taskbar, or home screen — works offline-aware, launches in its own window, no browser tabs.
        </p>
      </header>

      {/* State card */}
      {installed ? (
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
              <Check className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-emerald-300">Already installed</h2>
              <p className="text-xs text-emerald-300/70 mt-1">
                You're running haythix as an installed app right now. Updates apply automatically — you'll see a "New version available" toast when a fresh build deploys.
              </p>
              <p className="text-[11px] text-gray-500 mt-3">
                To install on another device (phone, tablet, second computer), open <span className="text-cyan-400 font-mono">haythix.com</span> there in Chrome/Edge/Safari and follow the steps below.
              </p>
            </div>
          </div>
        </div>
      ) : installable ? (
        <div className="bg-accent-primary/5 border border-accent-primary/30 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent-primary/15 flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-accent-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-white">Ready to install</h2>
              <p className="text-xs text-gray-400 mt-1">
                One click to install haythix as a native app — pinned to your taskbar or home screen with its own window.
              </p>
              <button
                onClick={handleInstall}
                disabled={busy}
                className="mt-4 px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-dark-900 text-sm font-bold hover:from-amber-300 hover:to-orange-300 disabled:opacity-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {busy ? 'Opening install prompt…' : 'Install haythix'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-amber-500/5 border border-amber-500/30 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-amber-300">Auto-install not available</h2>
              <p className="text-xs text-amber-200/80 mt-1">
                {browser.name} on this device doesn't expose a one-click install button. Use the manual steps below for your platform.
              </p>
              <p className="text-[11px] text-gray-500 mt-2">
                Detected: <span className="font-mono text-gray-400">{browser.name}{browser.isIOS ? ' / iOS' : browser.isAndroid ? ' / Android' : browser.isMac ? ' / macOS' : browser.isWindows ? ' / Windows' : ''}</span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Result banner */}
      {result && (
        <div className={`rounded-xl p-3 text-sm border ${
          result.type === 'ok' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' :
          result.type === 'warn' ? 'bg-amber-500/10 border-amber-500/30 text-amber-300' :
          'bg-blue-500/10 border-blue-500/30 text-blue-300'
        }`}>
          {result.msg}
          <button onClick={() => setResult(null)} className="float-right text-[11px] opacity-70 hover:opacity-100">dismiss</button>
        </div>
      )}

      {/* Per-platform manual instructions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Desktop (Chrome/Edge) */}
        <PlatformCard
          icon={Monitor}
          title="Windows / macOS — Chrome or Edge"
          highlight={!browser.isIOS && !browser.isAndroid && (browser.name === 'Chrome' || browser.name === 'Edge')}
          steps={[
            'Make sure you are on this page in Chrome or Edge (not Firefox).',
            'Look at the right end of the address bar — there is an install icon (a small computer/download symbol).',
            'Click it → click "Install" in the popup.',
            'haythix opens in its own window. You can find it later in your Start menu (Windows) or Applications folder (macOS).',
          ]}
          alt={browser.name === 'Chrome' || browser.name === 'Edge' ? null : (
            <p className="text-[11px] text-amber-300 mt-2">
              You're on {browser.name}. Firefox desktop doesn't support PWA install — switch to Chrome or Edge.
            </p>
          )}
        />

        {/* iOS Safari */}
        <PlatformCard
          icon={Smartphone}
          title="iPhone / iPad — Safari"
          highlight={browser.isIOS}
          steps={[
            <span key="1">Tap the <Share className="w-3.5 h-3.5 inline text-blue-400" /> Share button at the bottom of Safari.</span>,
            <span key="2">Scroll down and tap <Plus className="w-3.5 h-3.5 inline" /> <strong>Add to Home Screen</strong>.</span>,
            'Edit the name if you like, then tap Add.',
            'haythix lives on your home screen — opens full-screen with no Safari chrome.',
          ]}
        />

        {/* Android Chrome */}
        <PlatformCard
          icon={Smartphone}
          title="Android — Chrome"
          highlight={browser.isAndroid}
          steps={[
            'Tap the ⋮ menu (top right of Chrome).',
            'Tap "Install app" or "Add to Home screen".',
            'Confirm — the app icon appears on your home screen and app drawer.',
          ]}
        />

        {/* macOS Safari */}
        <PlatformCard
          icon={Monitor}
          title="macOS — Safari (16.4+)"
          highlight={browser.isMac && browser.name === 'Safari'}
          steps={[
            'In Safari, click File menu → Add to Dock.',
            'Edit the name + icon if you like, then click Add.',
            'haythix appears in your Dock and Applications folder.',
          ]}
        />
      </div>

      {/* Benefits */}
      <div className="bg-dark-800/40 rounded-2xl border border-dark-700/40 p-5">
        <h3 className="text-sm font-semibold text-white mb-3">Why install?</h3>
        <ul className="text-xs text-gray-400 space-y-2">
          <li className="flex gap-2">
            <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-gray-300">Own window</strong> — no browser tabs cluttering the trading view. Native window controls, dedicated taskbar/dock icon.</span>
          </li>
          <li className="flex gap-2">
            <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-gray-300">Faster cold-starts</strong> — installed apps cache the shell, so opening haythix is near-instant after the first load.</span>
          </li>
          <li className="flex gap-2">
            <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-gray-300">Auto-updates</strong> — when we deploy a new version, the app shows a "New version available" toast. One click to apply.</span>
          </li>
          <li className="flex gap-2">
            <Check className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
            <span><strong className="text-gray-300">Offline-aware</strong> — your most recent dashboards stay viewable if your connection drops. Trades obviously need network, but the UI doesn't crash.</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

function PlatformCard({ icon: Icon, title, highlight, steps, alt }) {
  return (
    <div className={`rounded-2xl border p-4 ${
      highlight ? 'bg-accent-primary/5 border-accent-primary/30' : 'bg-dark-800/40 border-dark-700/40'
    }`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className={`w-4 h-4 ${highlight ? 'text-accent-primary' : 'text-gray-500'}`} />
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        {highlight && (
          <span className="ml-auto text-[9px] font-bold px-2 py-0.5 rounded-full bg-accent-primary/15 text-accent-primary border border-accent-primary/30">
            YOUR DEVICE
          </span>
        )}
      </div>
      <ol className="text-xs text-gray-400 space-y-1.5 list-decimal list-inside">
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
      {alt}
    </div>
  );
}
